/**
 * iter211aw — Garage Hive imported case page (Phase 1 read-only).
 *
 * Shows the FOH the GH-sourced customer / vehicle / booking / lines /
 * groups + a "Required Yestimate Workflows" panel so they can
 * commission MOT / VI / Diagnostic / Service workflows on this case.
 *
 * A "Refresh from Garage Hive" button re-pulls the source jobsheet
 * without touching any Yestimate-generated workflow state.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  RefreshCw, Home, Wrench, Search, Stethoscope,
  ClipboardList, Loader2, AlertCircle, CheckCircle2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import DuplicateJobReconcileModal from "../../components/intake/DuplicateJobReconcileModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORKFLOW_DEFS = [
  { key: "mot",                label: "MOT / MCC",         icon: ClipboardList },
  { key: "vehicle_inspection", label: "Vehicle Inspection", icon: Search       },
  { key: "diagnostic",         label: "Diagnostic",         icon: Stethoscope  },
  { key: "service",            label: "Service Workflow",   icon: Wrench       },
];

function fmt(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

export default function GHCasePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [commissioning, setCommissioning] = useState(false);
  const [duplicateDetail, setDuplicateDetail] = useState(null);
  // iter213e — Live-refreshed FOH-typed "Job Instructions" from GH's
  // ``serviceComments`` entity. Fetched separately from the snapshot
  // so an intake imported before we started ingesting comments still
  // shows fresh instructions the moment the FE loads.
  const [liveInstructions, setLiveInstructions] = useState(null);
  const [liveInstructionsLoading, setLiveInstructionsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/foh/intake/${sessionId}/gh-snapshot`);
      setCaseData(res.data);
      const already = new Set(
        (res.data.commissioned_workflows || []).map((w) => w.key),
      );
      setSelected(already);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load this case.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // iter213e — Once the snapshot is loaded and we know the GH
  // jobsheet number, live-refresh the Job Instructions text from GH
  // so the tech/FOH always see what FOH typed most recently.
  useEffect(() => {
    const docNo = caseData?.gh_snapshot?.jobsheet?.gh_jobsheet_number;
    if (!docNo) return;
    let cancelled = false;
    setLiveInstructionsLoading(true);
    axios
      .get(`${API}/foh/integrations/garage-hive/jobsheet/${encodeURIComponent(docNo)}/instructions`)
      .then((r) => {
        if (!cancelled) setLiveInstructions(r.data);
      })
      .catch(() => {
        if (!cancelled) setLiveInstructions(null);
      })
      .finally(() => {
        if (!cancelled) setLiveInstructionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [caseData?.gh_snapshot?.jobsheet?.gh_jobsheet_number]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCommission = async () => {
    const workflows = Array.from(selected);
    if (workflows.length === 0) {
      toast.error("Pick at least one workflow to commission.");
      return;
    }
    setCommissioning(true);
    try {
      await axios.post(
        `${API}/foh/intake/${sessionId}/commission-workflows`,
        { workflows },
      );
      toast.success(`Commissioned ${workflows.length} workflow(s)`);
      await load();
    } catch (e) {
      // iter212b — the FOH must never see a raw duplicate-job 409.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" &&
          detail.code === "duplicate_active_job") {
        setDuplicateDetail(detail);
      } else {
        toast.error(typeof detail === "string" ? detail
                  : detail?.message || "Couldn't commission workflows.");
      }
    } finally {
      setCommissioning(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // iter213h — Two-step deliberate refresh:
      //   1) Snapshot refresh — pulls the latest jobsheet header
      //      (mileage, customer, status, etc.) without touching
      //      commissioned workflows (existing /gh-refresh contract).
      //   2) Additive workflow re-scan — reads EVERY GH comment +
      //      jobsheet line, runs the shared detector, and silently
      //      commissions any newly-detected workflows. Never
      //      removes or resets an existing / in-progress /
      //      completed workflow.
      await axios.post(`${API}/foh/intake/${sessionId}/gh-refresh`);
      const wf = await axios.post(
        `${API}/foh/intake/${sessionId}/refresh-from-garage-hive`,
      );
      const added = (wf?.data?.added || []);
      if (added.length > 0) {
        const _LABELS = {
          mot:                "MOT / MCC",
          vehicle_inspection: "Vehicle Inspection",
          diagnostic:         "Diagnostic Workflow",
          service:            "Service Workflow",
        };
        const list = added.map((k) => _LABELS[k] || k).join(", ");
        toast.success(
          `Garage Hive instructions refreshed. ${added.length} new workflow${added.length > 1 ? "s" : ""} added.`,
          { description: list },
        );
      } else {
        toast.success("Garage Hive instructions refreshed. No new workflows detected.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't refresh from Garage Hive.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-700" />
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-slate-800 font-semibold">{error || "Case not found."}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/app/foh")}
            data-testid="gh-case-back-to-foh"
          >
            Back to FOH
          </Button>
        </div>
      </div>
    );
  }

  const snap = caseData.gh_snapshot || {};
  const js   = snap.jobsheet || {};
  const veh  = snap.vehicle  || {};
  const cust = snap.customer || {};
  const commissioned = caseData.commissioned_workflows || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="max-w-5xl mx-auto px-5 py-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="inline-flex items-center gap-1.5 mb-1.5 px-2 py-0.5 rounded-full bg-teal-100/70 border border-teal-200/70">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600" />
              <p className="text-[10px] uppercase tracking-[0.22em] text-teal-800 font-semibold">
                Garage Hive · Imported
              </p>
            </div>
            <h1
              className="text-[26px] sm:text-[30px] font-bold tracking-tight text-slate-900"
              data-testid="gh-case-title"
            >
              {fmt(js.gh_jobsheet_number)} · {fmt(js.reg)}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              {fmt(js.customer_name)} · {fmt(veh.make || js.make_code)} {fmt(veh.model || js.model_code)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="gh-case-refresh"
            >
              {refreshing
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Refresh from Garage Hive
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/app/foh")}
              data-testid="gh-case-home"
            >
              <Home className="w-4 h-4 mr-1.5" />
              FOH
            </Button>
          </div>
        </div>

        {/* iter214a — FOH action banner. Surfaces when the tech has
            submitted at least one commissioned workflow and the FOH
            needs to review / send to pricing. Uses the same signals
            the deep-link resolver + Phase 3 projector use. */}
        {caseData?.foh_action_required && (
          <section
            className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4 mb-5 flex items-center justify-between gap-4 shadow-sm"
            data-testid="foh-action-required-banner"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-emerald-900">
                  Technician work submitted — FOH action required
                </div>
                <div className="text-sm text-emerald-800 mt-0.5">
                  Review the inspection and send this job to pricing.
                </div>
              </div>
            </div>
            {caseData.submitted_inspection_id && (
              <Button
                onClick={() => navigate(`/app/foh/inspection/${caseData.submitted_inspection_id}`)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white shrink-0"
                data-testid="foh-review-inspection-btn"
              >
                <Search className="w-4 h-4 mr-1.5" />
                Review Inspection
              </Button>
            )}
          </section>
        )}

        {/* GH Job header block */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
          data-testid="gh-case-header-block"
        >
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Garage Hive Job
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <KV label="Jobsheet"  value={fmt(js.gh_jobsheet_number)} />
            <KV label="Reg"       value={fmt(js.reg)} />
            <KV label="Customer"  value={fmt(js.customer_name)} />
            <KV label="Mileage"   value={fmt(js.mileage)} />
            <KV label="Booking"   value={`${fmt(js.booking_date)} ${fmt(js.booking_time)}`} />
            <KV label="Due"       value={fmt(js.due_date)} />
            <KV label="Status"    value={fmt(js.extended_status_code || js.status)} />
            <KV label="Advisor"   value={fmt(js.service_advisor)} />
          </div>
        </section>

        {/* Customer contact (iter212a) */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
          data-testid="gh-case-customer-block"
        >
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Customer Contact
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <KV label="Name"    value={fmt(cust.name || js.customer_name)} />
            <KV label="Type"    value={fmt(cust.customer_type)} />
            <KV label="Mobile"  value={fmt(cust.mobile || js.mobile_phone)} />
            <KV label="Phone"   value={fmt(cust.phone   || js.phone)} />
            <KV label="Email"   value={fmt(cust.email   || js.email)} />
            <KV label="Address" value={fmt(cust.address || js.sell_to_address)} />
            <KV label="City"    value={fmt(cust.city    || js.sell_to_city)} />
            <KV label="Postcode" value={fmt(cust.postcode || js.sell_to_postcode)} />
          </div>
        </section>

        {/* Vehicle block (iter212a) */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
          data-testid="gh-case-vehicle-block"
        >
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Vehicle
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <KV label="Make"          value={fmt(veh.make || js.make_code)} />
            <KV label="Model"         value={fmt(veh.model || js.model_code)} />
            <KV label="Year"          value={fmt(veh.production_year)} />
            <KV label="VIN"           value={fmt(veh.vin || js.vin)} />
            <KV label="Fuel"          value={fmt(veh.fuel_type)} />
            <KV label="Engine (cc)"   value={fmt(veh.engine_capacity)} />
            <KV label="Transmission"  value={fmt(veh.transmission)} />
            <KV label="MOT due"       value={fmt(veh.mot_due_date)} />
          </div>
        </section>

        {/* GH Instructions */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
          data-testid="gh-case-instructions"
        >
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Garage Hive Instructions
          </h2>
          {(() => {
            // iter213e — Prefer the LIVE refresh over the snapshot's
            // stale value so FOH updates in GH are reflected without
            // re-import. Fall back to the snapshot when the live call
            // hasn't returned yet or errored.
            const liveText = liveInstructions?.job_instructions;
            const snapshotText = js.job_instructions;
            const text = (liveText || snapshotText || "").trim();
            if (text) {
              return (
                <p
                  className="text-[15px] text-slate-800 whitespace-pre-wrap leading-relaxed"
                  data-testid="gh-case-instructions-text"
                >
                  {text}
                </p>
              );
            }
            if (liveInstructionsLoading) {
              return (
                <p className="text-sm text-slate-400 italic flex items-center gap-2"
                   data-testid="gh-case-instructions-loading">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Checking Garage Hive for the latest instructions…
                </p>
              );
            }
            if (js.customer_contact_notes) {
              return (
                <>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                    Customer contact notes
                  </p>
                  <p className="text-[15px] text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {js.customer_contact_notes}
                  </p>
                </>
              );
            }
            return (
              <p className="text-sm text-slate-400 italic"
                 data-testid="gh-case-instructions-empty">
                No instructions from FOH yet. Add a line tagged
                &quot;Job Instructions&quot; to the GH Comments box for
                this jobsheet.
              </p>
            );
          })()}
        </section>

        {/* Required Yestimate Workflows */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
          data-testid="gh-case-workflows"
        >
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Required Yestimate Workflows
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {WORKFLOW_DEFS.map(({ key, label, icon: Icon }) => {
              const isOn  = selected.has(key);
              const done  = commissioned.find((w) => w.key === key);
              // iter214a — Distinguish "commissioned" (teal) from
              // "the tech has actually completed this workflow"
              // (emerald + checkmark). Falls back to legacy behaviour
              // when the enrichment isn't available yet.
              const wfKey = key === "service" ? "vehicle_inspection" : key;
              const status = caseData?.workflow_status?.[wfKey] || {};
              const isCompleted  = !!done && !!status.completed;
              const isInProgress = !!done && !!status.in_progress;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggle(key)}
                  disabled={!!done}
                  className={`relative flex items-center gap-2 rounded-lg border-2 px-3 py-3 text-sm font-semibold transition-all ${
                    isCompleted
                      ? "bg-emerald-50 border-emerald-500 text-emerald-800 cursor-not-allowed"
                      : isInProgress
                      ? "bg-amber-50 border-amber-400 text-amber-900 cursor-not-allowed"
                      : done
                      ? "bg-slate-50 border-slate-300 text-slate-700 cursor-not-allowed"
                      : isOn
                      ? "bg-teal-50 border-teal-500 text-teal-800"
                      : "bg-white border-slate-200 text-slate-700 hover:border-teal-300"
                  }`}
                  data-testid={`gh-workflow-${key}`}
                >
                  {isCompleted && (
                    <CheckCircle2 className="absolute top-1 right-1 w-4 h-4 text-emerald-600" />
                  )}
                  {isInProgress && (
                    <Loader2 className="absolute top-1 right-1 w-4 h-4 text-amber-600 animate-spin" />
                  )}
                  <Icon className="w-4 h-4" />
                  <span className="flex-1 text-left">
                    {label}
                    {isCompleted && (
                      <span className="block text-[10px] font-normal opacity-80">
                        submitted — ready for review
                      </span>
                    )}
                    {isInProgress && (
                      <span className="block text-[10px] font-normal opacity-80">
                        tech in progress
                      </span>
                    )}
                    {done && !isCompleted && !isInProgress && (
                      <span className="block text-[10px] font-normal opacity-70">
                        commissioned — awaiting technician
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleCommission}
              disabled={commissioning || selected.size === 0}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="gh-case-commission-btn"
            >
              {commissioning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Create Technician Workflows
            </Button>
          </div>
        </section>

        {/* Existing GH lines / groups summary */}
        {(snap.jobsheet_lines || []).length > 0 && (
          <section
            className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
            data-testid="gh-case-existing-lines"
          >
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
              Existing Garage Hive Lines ({snap.jobsheet_lines.length})
            </h2>
            <ul className="divide-y divide-slate-100 text-sm">
              {snap.jobsheet_lines.slice(0, 30).map((l, i) => (
                <li key={l.gh_line_id || i} className="py-2 flex justify-between gap-3">
                  <span className="text-slate-800 truncate flex-1">{fmt(l.description)}</span>
                  <span className="text-slate-500">
                    {fmt(l.quantity)} × £{Number(l.unit_price || 0).toFixed(2)}
                  </span>
                  <span className="font-semibold text-slate-800">
                    £{Number(l.amount_including_vat ?? l.amount ?? 0).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {caseData.gh_last_synced_at && (
          <p className="text-[11px] text-slate-400 text-center">
            Last synced from Garage Hive: {new Date(caseData.gh_last_synced_at).toLocaleString()}
          </p>
        )}
      </div>

      {duplicateDetail && (
        <DuplicateJobReconcileModal
          detail={duplicateDetail}
          onGoBack={() => setDuplicateDetail(null)}
          onResolved={async () => {
            setDuplicateDetail(null);
            // Retry commissioning now that the existing job is cancelled.
            await handleCommission();
          }}
        />
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
